import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Search,
    Filter,
    Trash2,
    Heart,
    Share2,
    MapPin,
    Bed,
    Bath,
    Car,
    Square,
    Calendar,
    ExternalLink,
    Plus,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    Grid,
    Grid3x3,
    List,
    Edit,
    Home,
    Building2,
    Building,
    Trees,
    Tractor,
    Layers,
    TrendingUp,
    X,
    Zap,
    Coins,
    ArrowRight,
    Maximize2,
    Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getProperties, deleteProperty, patchProperty, getListingAgents } from "../../../api/agent";
import moment from "moment";
import toast from "react-hot-toast";
import { capitalizeEachWord } from "../../../helper/function";
import SkeletonPropertyCard from "../../../components/search/SkeletonPropertyCard";
import PropertyFilterTabs from "../components/PropertyFilterTabs";
import {
    DEFAULT_MARKET_TILES,
    DEFAULT_STATUS_TILES,
    MY_LISTINGS_MARKET_RULES,
    matchesTiles,
} from "../utils/propertyTiles";
import { cachedApiCall, cacheKeys, invalidateCache } from "../../../lib/cache";
import LazyImage from "../../../components/common/LazyImage";
import ManualSearchOverlay from "../components/ManualSearchOverlay";
import PropertySearchBar from "../components/PropertySearchBar";
import PropertyCard from "../components/PropertyCard";
import PropertyGridView from "../components/PropertyGridView";
import PropertyListView from "../components/PropertyListView";
import ConfirmModal from "../../../components/common/ConfirmModal";
import PropertySortDropdown from "../components/PropertySortDropdown";
import { DEFAULT_LIST_SORT, listSortParam } from "../utils/propertySort";
import { hasAdminAccess } from "../../../utils/roleAccess";

const notFoundImg =
    "https://atozpropertysolutionsltd.com/property-not-found.svg";

const PropertyListingPage = () => {
    const { user } = useSelector((state) => state.auth);

    const isAdmin = useMemo(() => hasAdminAccess(user), [user]);
    const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
    const [searchTerm, setSearchTerm] = useState("");
    const [properties, setProperties] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;
    const queryFiltersRef = useRef({});
    const loadMoreSentinelRef = useRef(null);
    // Every filter / tile / sort change starts a new generation. Load-more waits
    // for that generation's page 1, and a reply from an older generation is
    // dropped. Without this, switching Market or Sort after scrolling fired a
    // stale "page 2" alongside the new page 1 and mixed the two lists together.
    const fetchGenRef = useRef(0);
    const pageOneGenRef = useRef(-1);
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();
    const searchTimeout = useRef();
    const [imageIndexes, setImageIndexes] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [propertyToDelete, setPropertyToDelete] = useState(null);

    /*
     * Team switcher (Christian Gravias, Monday 13225574145: "Under Spiros My
     * listings he should be able to view George & Pauls properties so he can
     * have the options all agents, Spiros, Paul & George"). Only drawn for
     * someone with a team. "All agents" is the whole team (?team=true), a name
     * is that teammate (?team=true&agent=<id>), and the page still opens on the
     * person's own listings, as My Listings always has. The backend works the
     * team out itself, so nobody can switch past their own team.
     */
    const [teamAgents, setTeamAgents] = useState([]);
    const [viewing, setViewing] = useState("me"); // "me" | "team" | teammate id
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const switcherRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        getListingAgents({ scope: "team" })
            .then((res) => {
                if (!cancelled) setTeamAgents(Array.isArray(res.data?.agents) ? res.data.agents : []);
            })
            .catch(() => {
                if (!cancelled) setTeamAgents([]);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!switcherOpen) return;
        const close = (e) => {
            if (switcherRef.current && !switcherRef.current.contains(e.target)) {
                setSwitcherOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [switcherOpen]);

    const hasTeam = teamAgents.length > 1;
    const viewingMember = teamAgents.find((a) => String(a.id) === String(viewing)) || null;
    const switcherLabel = viewing === "team"
        ? "All agents"
        : viewingMember?.name || teamAgents.find((a) => a.is_me)?.name || "Me";
    const pageTitle = viewing === "team"
        ? "Team Listings"
        : viewingMember && !viewingMember.is_me ? `${viewingMember.name}'s Listings` : "My Listings";
    const teamParams = useMemo(() => {
        if (viewing === "team") return { team: true };
        if (viewingMember && !viewingMember.is_me) return { team: true, agent: viewingMember.id };
        return {};
    }, [viewing, viewingMember]);

    const pickViewing = (value) => {
        setViewing(value);
        setSwitcherOpen(false);
    };

    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        listingType: 'all', // 'all', 'public', 'offmarket'
        suburb: '',
        minBeds: '',
        minBaths: '',
        minCarSpaces: '',
        priceMin: '',
        priceMax: ''
    });

    // New state for UI revamp
    // Status row (All / For Sale / For Lease / Appraisal / Sold / Leased /
    // Withdrawn) and the Market row under it. Opens on For Sale, as before.
    const [selectedStatuses, setSelectedStatuses] = useState(DEFAULT_STATUS_TILES);
    const [selectedMarkets, setSelectedMarkets] = useState(DEFAULT_MARKET_TILES);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    // The Sort dropdown above the search bar. The backend orders the list, so
    // every page that loads follows on from the last (utils/propertySort.js).
    const [listSort, setListSort] = useState(DEFAULT_LIST_SORT);
    // A pick from the dropdown takes over from any column-header sort, which
    // only re-orders the rows already loaded.
    const handleListSortChange = (next) => {
        setListSort(next);
        setSortConfig({ key: null, direction: "asc" });
    };

    // Advanced Filter State
    const [buyerType, setBuyerType] = useState("owner");
    const [advFilters, setAdvFilters] = useState({
        propertyTypes: ["all"],
        priceRange: { min: "Any", max: "Any" },
        bedrooms: { min: "Any", max: "Any" },
        useBedroomRange: false,
        bathrooms: "Any",
        carSpaces: "Any",
        landSize: { min: "Any", max: "Any" },
        saleMethod: "All",
        excludeUnderContract: false,
        targetYield: "Any",
        capitalGrowth: "Any",
        showWithPriceOnly: false,
    });

    const handleToggleFilter = (current, setFunction, value) => {
        if (current.includes(value)) {
            const next = current.filter(v => v !== value);
            if (next.length === 0) {
                setFunction(["all"]);
            } else {
                setFunction(next);
            }
        } else {
            setFunction([...current, value]);
        }
    };

    const getDaysBadgeColorLocal = (days) => {
        if (days <= 14) return "bg-[#00D094] text-white";
        if (days <= 30) return "bg-[#FF8E26] text-white";
        return "bg-[#EE3E34] text-white";
    };

    const getBorderColorLocal = (days) => {
        if (days <= 14) return "border-[#00D094]";
        if (days <= 30) return "border-[#FF8E26]";
        return "border-[#EE3E34]";
    };

    const handleSort = (key, explicitDirection = null) => {
        let direction = explicitDirection;
        if (!direction) {
            direction = "asc";
            if (sortConfig.key === key && sortConfig.direction === "asc") {
                direction = "desc";
            }
        }
        setSortConfig({ key, direction });
    };

    const fetchProperties = async (page = 1, filters = {}, options = {}) => {
        const { append = false } = options;
        const gen = fetchGenRef.current;
        if (!append) {
            setLoading(true);
            setError(null);
        } else {
            setLoadingMore(true);
        }
        try {
            const params = {
                search: filters.search ?? "",
                page,
                page_size: itemsPerPage,
                // Appraisal-status properties are asked for ONLY when the
                // Appraisal tab is open or the agent is searching - see the
                // comment on wantsAppraisals below. Otherwise they'd take up
                // page slots that belong to the actual listings.
                include_appraisals: filters.include_appraisals ?? false,
                ...(filters.sort ? { sort: filters.sort } : {}),
                ...(filters.team ? { team: true } : {}),
                ...(filters.agent ? { agent: filters.agent } : {}),
            };
            const res = await cachedApiCall(
                cacheKeys.properties(params),
                () => getProperties(params),
                3 * 60 * 1000
            );

            if (gen !== fetchGenRef.current) return;
            if (!append) pageOneGenRef.current = gen;
            const data = res.data;
            const items = Array.isArray(data)
                ? data
                : Array.isArray(data?.results)
                    ? data.results
                    : [];

            if (append) {
                setProperties(prev => [...prev, ...items]);
            } else {
                setProperties(items);
            }
            setNextPage(data?.next || null);
            setCurrentPage(data?.page ?? page);
            if (data?.count !== undefined) setTotalCount(data.count);
        } catch (err) {
            setError("Failed to load properties");
        } finally {
            // A dropped (older) page 1 leaves the spinner to the newer one.
            if (append) setLoadingMore(false);
            else if (gen === fetchGenRef.current) setLoading(false);
        }
    };

    const loadMore = useCallback(() => {
        if (loadingMore || loading || !nextPage) return;
        if (pageOneGenRef.current !== fetchGenRef.current) return;
        fetchProperties(currentPage + 1, queryFiltersRef.current, { append: true });
    }, [currentPage, nextPage, loadingMore, loading]);

    const filteredProperties = useMemo(() => {
        let result = [...properties];

        if (filters.suburb) {
            const suburb = filters.suburb.toLowerCase();
            result = result.filter(p =>
                (p.suburb || "").toLowerCase().includes(suburb) ||
                (p.postcode || "").includes(suburb) ||
                (p.state || "").toLowerCase().includes(suburb) ||
                (p.address || "").toLowerCase().includes(suburb)
            );
        }

        if (!advFilters.propertyTypes.includes("all")) {
            result = result.filter(p => {
                const type = (p.property_type || "").toLowerCase();
                return advFilters.propertyTypes.some(t => type.includes(t.toLowerCase()));
            });
        }

        if (advFilters.priceRange.min !== "Any") {
            const min = parseFloat(advFilters.priceRange.min);
            result = result.filter(p => {
                const price = typeof p.price === 'string' ? parseFloat(p.price.replace(/[^0-9.-]+/g, '')) : p.price;
                return price >= min;
            });
        }

        if (advFilters.priceRange.max !== "Any") {
            const max = parseFloat(advFilters.priceRange.max);
            result = result.filter(p => {
                const price = typeof p.price === 'string' ? parseFloat(p.price.replace(/[^0-9.-]+/g, '')) : p.price;
                return price <= max;
            });
        }

        if (advFilters.bedrooms.min !== "Any") {
            result = result.filter(p => (p.beds || 0) >= parseInt(advFilters.bedrooms.min));
        }
        if (advFilters.bedrooms.max !== "Any") {
            result = result.filter(p => (p.beds || 0) <= parseInt(advFilters.bedrooms.max));
        }

        if (advFilters.bathrooms !== "Any") {
            result = result.filter(p => (p.baths || 0) >= parseInt(advFilters.bathrooms));
        }

        if (advFilters.carSpaces !== "Any") {
            result = result.filter(p => (p.parkings || 0) >= parseInt(advFilters.carSpaces));
        }

        if (advFilters.saleMethod !== "All") {
            result = result.filter(p => {
                const method = (p.method_of_sale || p.status || "").toLowerCase();
                return method.includes(advFilters.saleMethod.toLowerCase());
            });
        }

        if (advFilters.excludeUnderContract) {
            result = result.filter(p => (p.status || "").toLowerCase() !== "under contract");
        }

        if (advFilters.showWithPriceOnly) {
            result = result.filter(p => p.price && p.price !== "0");
        }

        // Status / Market tiles, multi-select (Christian Gravias, 1 Oct 2026).
        // Same rule as Collings Properties, with My Listings' own market split
        // (a Prospect counts as off market here). Withdrawn listings sit in the
        // Withdrawn tile alone, Sold and Leased in theirs, and "All" is every
        // live listing unless the agent is searching.
        result = result.filter(p => matchesTiles(p, {
            statuses: selectedStatuses,
            markets: selectedMarkets,
            searching: Boolean(searchTerm),
            marketRules: MY_LISTINGS_MARKET_RULES,
        }));

        // APPRAISALS LIVE IN THE PIPELINE, NOT HERE.
        // Simon Abbott, 3 Sep 2026: "Appraisals should not go into Properties.
        // Make them searchable but only have them sit in pipeline so they don't
        // clock up the Properties page." Once the office has hundreds of them
        // they bury the actual listings. So they are dropped from every tab
        // EXCEPT the Appraisal tab (which exists to list them) and any active
        // search, which is how "still searchable" is honoured.
        if (!selectedStatuses.includes("appraisal") && !searchTerm) {
            result = result.filter(p => {
                const statusName = (p.status?.name || p.status || "").toLowerCase();
                return !statusName.includes("appraisal");
            });
        }

        // Hide Buyer Portal "Sell Your Property" appraisal placeholders from My Listings,
        // except when the Appraisal tab is active (those leads ARE appraisals and should show there).
        if (!selectedStatuses.includes("appraisal")) {
            result = result.filter(p => {
                const description = (p.description || "").toLowerCase();
                return !description.includes("sell your property lead");
            });
        }

        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Map sort keys to actual property keys if needed
                if (sortConfig.key === 'parking') {
                    aValue = a.parkings || a.parking;
                    bValue = b.parkings || b.parking;
                } else if (sortConfig.key === 'type') {
                    aValue = a.property_type || a.type;
                    bValue = b.property_type || b.type;
                } else if (sortConfig.key === 'area') {
                    aValue = a.floor_area || a.area || a.building_area;
                    bValue = b.floor_area || b.area || b.building_area;
                } else if (sortConfig.key === 'gross_yield') {
                    aValue = a.gross_yield || a.grossYield || a.est_gross_yield;
                    bValue = b.gross_yield || b.grossYield || b.est_gross_yield;
                } else if (sortConfig.key === 'net_yield') {
                    aValue = a.net_yield || a.netYield || a.est_net_yield;
                    bValue = b.net_yield || b.netYield || b.est_net_yield;
                }

                // Handle numeric string conversions
                const numericKeys = ['price', 'beds', 'baths', 'parkings', 'area', 'land_area', 'rent', 'gross_yield', 'net_yield'];
                if (numericKeys.includes(sortConfig.key) || sortConfig.key === 'parking' || sortConfig.key === 'floor_area') {
                    const parseVal = (val) => {
                        if (typeof val === 'number') return val;
                        if (!val) return 0;
                        if (typeof val === 'string') {
                            return parseFloat(val.replace(/[^0-9.-]+/g, '')) || 0;
                        }
                        return 0;
                    };
                    aValue = parseVal(aValue);
                    bValue = parseVal(bValue);
                }

                if ((aValue || 0) > (bValue || 0)) return sortConfig.direction === "asc" ? 1 : -1;
                if ((aValue || 0) < (bValue || 0)) return sortConfig.direction === "asc" ? -1 : 1;
                return 0;
            });
        }

        return result;
    }, [properties, advFilters, filters, sortConfig, selectedStatuses, selectedMarkets, searchTerm]);

    const handleClearFilters = () => {
        setFilters({
            listingType: 'all',
            suburb: '',
            minBeds: '',
            minBaths: '',
            minCarSpaces: '',
            priceMin: '',
            priceMax: ''
        });
        setAdvFilters({
            propertyTypes: ["all"],
            priceRange: { min: "Any", max: "Any" },
            bedrooms: { min: "Any", max: "Any" },
            useBedroomRange: false,
            bathrooms: "Any",
            carSpaces: "Any",
            landSize: { min: "Any", max: "Any" },
            saleMethod: "All",
            excludeUnderContract: false,
            targetYield: "Any",
            capitalGrowth: "Any",
            showWithPriceOnly: false,
        });
        setSearchTerm("");
        setSelectedStatuses(DEFAULT_STATUS_TILES);
        setSelectedMarkets(DEFAULT_MARKET_TILES);
    };

    const paginatedProperties = filteredProperties;

    // Appraisals belong in the pipeline, not on this page (Simon Abbott, 3 Sep
    // 2026). Only fetch them when the Appraisal tab is open or the agent is
    // searching for one - which is how they stay findable from here.
    const wantsAppraisals = selectedStatuses.includes("appraisal") || Boolean(searchTerm.trim());

    const buildQueryFilters = useCallback(
        () => ({ search: searchTerm.trim(), include_appraisals: wantsAppraisals, ...listSortParam(listSort), ...teamParams }),
        [searchTerm, wantsAppraisals, listSort, teamParams]
    );

    useEffect(() => {
        setCurrentPage(1);
        setProperties([]);
        fetchGenRef.current += 1;
        const queryFilters = buildQueryFilters();
        queryFiltersRef.current = queryFilters;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => fetchProperties(1, queryFilters), 400);
        return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
    }, [searchTerm, buildQueryFilters]);

    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        if (!sentinel || !nextPage) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && nextPage && !loadingMore && !loading) loadMore();
            },
            { root: null, rootMargin: "200px", threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [nextPage, loadingMore, loading, loadMore]);

    const handleDeleteClick = (property) => {
        setPropertyToDelete(property);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!propertyToDelete) return;

        const deletedId = propertyToDelete.id;
        setDeletingId(deletedId);
        try {
            await deleteProperty(deletedId);
            // Immediately remove from local state (no refresh needed)
            setProperties(prev => prev.filter(p => p.id !== deletedId));
            invalidateCache.properties();
            toast.success("Property deleted successfully");
            setShowDeleteModal(false);
            setPropertyToDelete(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to delete property");
            setShowDeleteModal(false);
            setPropertyToDelete(null);
        } finally {
            setDeletingId(null);
        }
    };

    const handleEdit = (property) => {
        navigate(`/properties/edit/${property.id}`);
    };

    const handleStageUpdate = async (propertyId, newStage) => {
        try {
            await patchProperty(propertyId, { status: newStage });
            toast.success("Stage updated successfully");
            // Update local state to reflect change immediately without full fetch if possible
            setProperties(prev => prev.map(p =>
                p.id === propertyId ? { ...p, status: newStage } : p
            ));
            invalidateCache.properties();
        } catch (err) {
            toast.error("Failed to update stage");
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setPropertyToDelete(null);
    };

    const getDaysOnMarket = (createdAt) => {
        if (!createdAt) return 0;
        const created = moment(createdAt);
        const now = moment();
        return now.diff(created, 'days');
    };

    return (
        <div className="min-h-screen bg-[#FFFFFF] p-0 sm:p-0 text-[#36423F] -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6 w-auto max-w-full overflow-x-hidden">
            <div className="bg-[#F8F8F8] px-4 sm:px-6 md:px-6 lg:px-8 py-4 sm:py-5 md:py-4 lg:py-5 border-b border-gray-100">
                <div className="flex flex-col gap-3 sm:gap-4 md:gap-3 lg:gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#55C5D0]">{pageTitle}</h1>
                            {hasTeam && (
                                <div className="relative" ref={switcherRef}>
                                    <button
                                        type="button"
                                        onClick={() => setSwitcherOpen((o) => !o)}
                                        title="Switch between your team"
                                        aria-haspopup="true"
                                        aria-expanded={switcherOpen}
                                        className="px-3 py-1.5 rounded-lg border border-[#E6E7E9] bg-white text-xs font-bold text-[#353B48] flex items-center gap-1.5"
                                    >
                                        <Users size={13} className="text-[#55C5D0]" />
                                        {switcherLabel}
                                        <ChevronDown size={13} className="text-[#9CA3AF]" />
                                    </button>
                                    {switcherOpen && (
                                        <div className="absolute left-0 mt-1 w-72 max-w-[calc(100vw-2rem)] bg-white border border-[#E6E7E9] rounded-xl shadow-xl z-40 py-1 max-h-80 overflow-y-auto custom-scrollbar">
                                            <button
                                                type="button"
                                                onClick={() => pickViewing("team")}
                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                                                    viewing === "team" ? "font-bold text-[#0e7d86]" : "text-[#353B48]"
                                                }`}
                                            >
                                                All agents
                                            </button>
                                            <div className="border-t border-gray-100 my-1" />
                                            <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-[#9CA3AF] font-bold">
                                                Team · listings
                                            </p>
                                            {teamAgents.map((agent) => {
                                                const active = agent.is_me
                                                    ? viewing === "me" || String(viewing) === String(agent.id)
                                                    : String(viewing) === String(agent.id);
                                                return (
                                                    <button
                                                        key={agent.id}
                                                        type="button"
                                                        onClick={() => pickViewing(agent.is_me ? "me" : agent.id)}
                                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                                                            active ? "font-bold text-[#0e7d86]" : "text-[#353B48]"
                                                        }`}
                                                    >
                                                        <span className="truncate">{agent.name}{agent.is_me ? " (me)" : ""}</span>
                                                        <span className="text-[10px] text-[#9CA3AF] shrink-0">{agent.count}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                            <div className="bg-[#EFEFEF] p-1 rounded-full flex items-center h-10 sm:h-12">
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 rounded-full transition-all text-xs sm:text-sm ${viewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                >
                                    <List className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="font-bold">List</span>
                                </button>
                                <button
                                    onClick={() => setViewMode("grid")}
                                    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 rounded-full transition-all text-xs sm:text-sm ${viewMode === "grid" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                >
                                    <Grid3x3 className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="font-bold">Grid</span>
                                </button>
                            </div>

                            <button
                                onClick={() => navigate('/properties/add-property')}
                                className="bg-[#36423F] hover:bg-[#2d3a37] text-white h-10 sm:h-12 px-4 sm:px-8 rounded-full font-bold shadow-lg transition-all flex items-center gap-2 active:scale-95 whitespace-nowrap text-xs sm:text-sm"
                            >
                                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                                <span>Add Property</span>
                            </button>
                        </div>
                    </div>

                    <PropertyFilterTabs
                        selectedStatuses={selectedStatuses}
                        setSelectedStatuses={setSelectedStatuses}
                        selectedMarkets={selectedMarkets}
                        setSelectedMarkets={setSelectedMarkets}
                    />
                </div>
            </div>

            <div className="px-4 sm:px-6 py-4 sm:py-6 md:py-5 w-full overflow-hidden">
                <div className="relative mb-10">
                    {/* Sort sits on top of the search bar and settings, on every
                        screen size (Christian Gravias, 7 Oct 2026). */}
                    <div className="flex justify-end mb-3">
                        <PropertySortDropdown value={listSort} onChange={handleListSortChange} />
                    </div>
                    <PropertySearchBar
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onSearchClick={() => fetchProperties(searchTerm)}
                        onFilterClick={() => setShowFilters(!showFilters)}
                        showFilters={showFilters}
                        placeholder="Search Address or Property Features with AI"
                        placeholderMobile="Search address or features"
                        searchButtonLabel="Search"
                    />
                    <ManualSearchOverlay
                        showFilters={showFilters}
                        setShowFilters={setShowFilters}
                        buyerType={buyerType}
                        setBuyerType={setBuyerType}
                        advFilters={advFilters}
                        setAdvFilters={setAdvFilters}
                        filters={filters}
                        setFilters={setFilters}
                        onClear={handleClearFilters}
                    />
                </div>

                <div className="mb-10">
                    {loading && !error ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
                                    <div className="h-48 bg-gray-100 rounded-lg mb-4"></div>
                                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-100 rounded w-1/2 mb-4"></div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <div className="text-red-500 mb-2">Error loading properties ({error})</div>
                            <button onClick={() => fetchProperties()} className="text-[#55C5D0] font-bold">Try again</button>
                        </div>
                    ) : filteredProperties.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-dashed border-gray-200">
                            <span className="text-5xl mb-4">🏠</span>
                            <span className="text-gray-900 text-xl font-bold mb-2">No properties found</span>
                            <p className="text-gray-400">Try adjusting your filters or search terms</p>
                        </div>
                    ) : viewMode === "grid" ? (
                        <PropertyGridView
                            properties={paginatedProperties}
                            getDaysOnMarket={getDaysOnMarket}
                            onEdit={handleEdit}
                            onDelete={handleDeleteClick}
                        />
                    ) : (
                        <PropertyListView
                            properties={paginatedProperties}
                            handleSort={handleSort}
                            navigate={navigate}
                            capitalizeEachWord={capitalizeEachWord}
                            onEdit={handleEdit}
                            sortConfig={sortConfig}
                            onStageUpdate={handleStageUpdate}
                        />
                    )}

                </div>

                {filteredProperties.length > 0 && (
                    <div className="flex justify-center mt-6">
                        <span className="text-sm text-gray-500">
                            {filteredProperties.length === properties.length ? (
                                <>Showing {filteredProperties.length} active listings</>
                            ) : (
                                <>Showing {filteredProperties.length} of {properties.length} loaded properties (filtered)</>
                            )}
                            {nextPage && <span className="ml-1 text-gray-400">• Scroll for more</span>}
                        </span>
                    </div>
                )}

                {nextPage && !error && (
                    <>
                        <div ref={loadMoreSentinelRef} className="h-10 flex items-center justify-center mt-6" aria-hidden="true" />
                        {!loadingMore && (
                            <div className="flex justify-center mt-4">
                                <button
                                    onClick={loadMore}
                                    className="px-6 py-2.5 bg-[#55C5D0] hover:bg-[#45b5c0] text-white rounded-lg font-bold text-sm transition-all"
                                >
                                    Load More Properties
                                </button>
                            </div>
                        )}
                    </>
                )}

                {loadingMore && (
                    <div className="flex items-center justify-center py-6 gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#55C5D0] border-t-transparent" />
                        <span className="text-sm text-gray-500">Loading more properties...</span>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                loading={deletingId === propertyToDelete?.id}
                title="Delete Property"
                description={`Are you sure you want to permanently delete "${propertyToDelete?.address || propertyToDelete?.title || 'this property'}"? This action cannot be undone and all associated data will be removed.`}
                confirmText="Delete Property"
                variant="danger"
            />

        </div>
    );
};

export default PropertyListingPage;
